import React, { useState } from "react";
import { ArrowLeft, Plus, Inbox, MoveUp, MoveDown, GripVertical, ArrowUpDown, Edit3 } from "lucide-react";
import { useLazyGetAddOnsByPackageIdQuery, useGetAllPackagesQuery, useUpdateAddOnOrderStatusMutation, useUpdateAddOnStatusMutation } from "../../../redux/actions/Admin/ServiceAdmin";
import CommonButton from "../../../components/CommonButton";
import AddOnsFormScreen from "./AddsOnFormScreen";
import { useAlert } from "../../../components/customer/AlertContext";
import { IGetAddOnsResponse } from "../../../types/Response/AdminResponse";

const ManageAddOns: React.FC = () => {
    const { data, isLoading, isError } = useGetAllPackagesQuery();

    // Workflow state controls updated to handle form view cleanly
    const [view, setView] = useState<'packages-select' | 'addons-workspace' | 'addon-form'>('packages-select');
    const [selectedPackage, setSelectedPackage] = useState<any>(null);
    const [updateAddOnStatus, { isLoading: isUpdatingStatus }] =
        useUpdateAddOnStatusMutation();
    const { showConfirm } = useAlert();
    const [isReordering, setIsReordering] = useState(false);
    const [reorderList, setReorderList] = useState<IGetAddOnsResponse[]>([]);
    const [updateAddOnOrderStatus] = useUpdateAddOnOrderStatusMutation();
    const [selectedAddOnId, setSelectedAddOnId] =
        useState<number | null>(null);

const [
    getAddOnsByPackageId,
    {
        data: addOns = [],
        isLoading: isAddOnsLoading,
    },
] = useLazyGetAddOnsByPackageIdQuery();

    const handleStartReorder = () => {
        setReorderList([...activeAddOns]);
        setIsReordering(true);
    };

    const moveUp = (index: number) => {
        if (index === 0) return;

        const updated = [...reorderList];

        [updated[index - 1], updated[index]] =
            [updated[index], updated[index - 1]];

        setReorderList(updated);
    };

    const moveDown = (index: number) => {
        if (index === reorderList.length - 1) return;

        const updated = [...reorderList];

        [updated[index + 1], updated[index]] =
            [updated[index], updated[index + 1]];

        setReorderList(updated);
    };

    const handleSaveOrder = async () => {
        try {
            const payload = reorderList.map((item, index) => ({
                id: item.id,
                sortOrder: index + 1,
            }));

            await updateAddOnOrderStatus(payload).unwrap();

            setIsReordering(false);

            showConfirm({
                type: "success",
                title: "Order Updated",
                message: "Add-On order updated successfully.",
                showCancel: false,
            });
        } catch (error) {
            console.log(error);

            showConfirm({
                type: "error",
                title: "Update Failed",
                message: "Unable to update add-on order.",
                showCancel: false,
            });
        }
    };

    if (isLoading) {
        return <div className="p-8 text-center text-gray-400 font-semibold">Loading packages...</div>;
    }

    if (isError) {
        return <div className="p-8 text-center text-red-500 font-semibold">Failed to load packages</div>;
    }

    const activeAddOns = addOns.filter(addon => addon.isActive);
    const inactiveAddOns = addOns.filter(addon => !addon.isActive);

const handleViewAddOns = async (pkg: any) => {
    setSelectedPackage(pkg);
    setView("addons-workspace");

    try {
        await getAddOnsByPackageId(pkg.id).unwrap();
    } catch (error) {
        console.error(error);
    }
};
    const handleEditAddOn = (addon: IGetAddOnsResponse) => {
        setSelectedAddOnId(addon.id);
        setView("addon-form");
    };

    const handleBackToPackages = () => {
        setSelectedPackage(null);
        setView('packages-select');
    };

    const handleToggleAddOnStatus = async (
        id: number,
        isActive: boolean
    ) => {
        try {
            await updateAddOnStatus({
                id,
                isActive: !isActive,
            }).unwrap();

            showConfirm({
                type: "success",
                title: "Status Updated",
                message: `Add-On ${!isActive ? "activated" : "deactivated"} successfully.`,
                showCancel: false,
            });
        } catch (error) {
            showConfirm({
                type: "error",
                title: "Update Failed",
                message: "Unable to update Add-On status.",
                showCancel: false,
            });
        }
    };


    // =========================================================
    // VIEW 1: PACKAGES SELECT GRID
    // =========================================================
    if (view === 'packages-select') {
        return (
            <div className="p-4 min-h-screen">
                <div className="border-b border-gray-300/40 pb-4 mb-4">
                    <h1 className="text-2xl md:text-3xl font-bold text-logoHeading">
                        Add-On Management
                    </h1>
                    <p className="text-gray-400 mt-1 text-xs md:text-sm font-medium">
                        Create, organize, and manage optional add-ons available across packages.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {data?.map((pkg, index) => {
                        const firstPrice = pkg.pricing?.[0];

                        return (
                            <div
                                key={pkg.id}
                                className={`rounded-3xl border bg-white px-6 py-3 transition-all
                                ${pkg.isActive ? "border-lime-400" : "border-gray-200"}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex gap-2">
                                        <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-lg text-sm font-semibold">
                                            Package {index + 1}
                                        </span>
                                        {pkg.validForMonths >= 12 && (
                                            <span className="bg-orange-100 text-orange-600 px-3 py-1 rounded-lg text-sm font-semibold">
                                                🔥 LONG TERM
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="py-2">
                                    <h2 className="text-2xl font-semibold text-logoHeading">
                                        {pkg.packageTitle}
                                    </h2>
                                    <p className="text-mainBrandColor mt-2 text-base">
                                        {pkg.packageSubtitle}
                                    </p>
                                    <p className="text-gray-800 mt-2 text-sm">
                                        {pkg.description}
                                    </p>
                                </div>

                                <div className="py-2">
                                    <h3 className="text-iconColor text-base font-semibold mb-4">
                                        Includes:
                                    </h3>
                                    <div className="flex flex-wrap gap-2">
                                        {pkg.includes?.map((item, idx) => (
                                            <span
                                                key={idx}
                                                className="bg-amber-50 text-amber-700 px-3 py-2 rounded-xl text-xs"
                                            >
                                                • {item}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="border-t mt-2 pt-2 pb-2">
                                    <div className="flex justify-between items-end">
                                        <div>
                                            <div className="">
                                                <span className="text-2xl font-bold text-logoHeading">
                                                   AED {firstPrice.originalPrice}
                                                </span>
                                               
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <CommonButton
                                    text="View Add-Ons"
                                    variant="filled"
                                    rounded="md"
                                    className="w-full"
                                    onClick={() => handleViewAddOns(pkg)}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // =========================================================
    // VIEW 2: WORKSPACE VIEW FOR SINGLE PACKAGE ADD-ONS
    // =========================================================
    if (view === 'addons-workspace' && selectedPackage) {
        return (
            <div className="p-4 min-h-screen">
                <div className="max-w-[1400px] mx-auto space-y-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-gray-300/40 pb-5">
                        <div className="space-y-1">
                            <button
                                onClick={handleBackToPackages}
                                className="inline-flex items-center gap-1 text-xs font-bold text-gray-400 hover:text-logoHeading uppercase tracking-wider transition cursor-pointer"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Switch Packages Selection
                            </button>
                            <h1 className="text-xl md:text-2xl font-black text-logoHeading tracking-tight">
                                {selectedPackage.packageTitle} <span className="font-semibold text-mainBrandColor">/ Custom Add-Ons Desk</span>
                            </h1>
                        </div>

                        <CommonButton
                            text="Create Add-On"
                            onClick={() => setView('addon-form')}
                            variant="filled"
                            icon={<Plus className="w-4 h-4 stroke-[2.5]" />}
                            rounded="md"
                            iconPosition="left"
                            iconClassName="px-2 py-2"
                        />
                    </div>

                    {isAddOnsLoading ? (
                        <div className="text-center py-10">
                            Loading add-ons...
                        </div>
                    ) : addOns.length > 0 ? (
                        <div className="space-y-8">
                            {/* Active Add-Ons */}
                            <div>
                                <div className="flex flex-col items-center gap-4 lg:flex-row lg:items-center lg:justify-between mb-4">
                                    <div className="flex items-center gap-4">
                                        <h2 className="text-lg font-bold text-logoHeading">
                                            Active Add-Ons
                                        </h2>
                                        <span className="px-2 py-0.5 rounded-full bg-lime-100 text-lime-700 text-base font-bold">
                                            {activeAddOns.length}
                                        </span>
                                    </div>

                                    {!isReordering && activeAddOns.length > 0 && (
                                        <CommonButton
                                            text="Update Order"
                                            onClick={handleStartReorder}
                                            variant="filled"
                                            rounded="md"
                                            className="bg-mainBrandColor"
                                            icon={<ArrowUpDown className="w-4 h-4" />}
                                        />
                                    )}
                                </div>

                                {activeAddOns.length > 0 ? (
                                    <div className="space-y-4">
                                        {(isReordering ? reorderList : activeAddOns).map((addon, index) => (
                                            <div
                                                key={addon.id}
                                                className={`bg-white rounded-xl border overflow-hidden flex flex-col lg:flex-row items-center p-2 gap-4 transition-all duration-200 ${isReordering ? "border-mainBrandColor/40 shadow-sm bg-slate-50/50" : addon.isActive ? "border-lime-400" : "border-gray-200"
                                                    }`}
                                            >
                                                {/* Reorder Premium Controls Block */}
                                                {isReordering && (
                                                    <div className="flex items-center gap-1 pl-2 shrink-0 border-r border-gray-200/60 pr-2">
                                                        <GripVertical className="w-4 h-4 text-gray-400 mr-1" />
                                                        <div className="flex flex-col gap-1">
                                                            <button
                                                                type="button"
                                                                disabled={index === 0}
                                                                onClick={() => moveUp(index)}
                                                                className={`p-1.5 border rounded-lg transition ${index === 0
                                                                    ? "bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed"
                                                                    : "bg-white border-gray-200 text-gray-600 hover:text-mainBrandColor hover:border-mainBrandColor/40 hover:shadow-sm"
                                                                    }`}
                                                            >
                                                                <MoveUp className="w-3.5 h-3.5" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                disabled={index === (isReordering ? reorderList.length : activeAddOns.length) - 1}
                                                                onClick={() => moveDown(index)}
                                                                className={`p-1.5 border rounded-lg transition ${index === (isReordering ? reorderList.length : activeAddOns.length) - 1
                                                                    ? "bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed"
                                                                    : "bg-white border-gray-200 text-gray-600 hover:text-mainBrandColor hover:border-mainBrandColor/40 hover:shadow-sm"
                                                                    }`}
                                                            >
                                                                <MoveDown className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="w-full lg:w-28 h-40 lg:h-24 overflow-hidden flex-shrink-0 rounded-lg">
                                                    <img
                                                        src={addon.imageUrl || "/placeholder-addon.jpg"}
                                                        alt={addon.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>

                                                <div className="flex-1 p-2 w-full">
                                                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                                                        <div className="min-w-0">
                                                            <h3 className="text-lg md:text-xl font-bold text-logoHeading">
                                                                {addon.name}
                                                            </h3>
                                                            <p className="text-gray-500 text-sm mt-1">
                                                                {addon.description}
                                                            </p>
                                                        </div>

                                                        <div className="flex items-center justify-between lg:justify-end gap-4">
                                                            <span className="text-xl md:text-2xl font-bold text-mainBrandColor whitespace-nowrap">
                                                                AED {addon.price}
                                                            </span>

                                                            {!isReordering && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleEditAddOn(addon)}
                                                                    className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                                                                >
                                                                    <Edit3 className="w-4 h-4 text-logoHeading" />
                                                                </button>
                                                            )}

                                                            <button
                                                                type="button"
                                                                disabled={isUpdatingStatus || isReordering}
                                                                onClick={() =>
                                                                    handleToggleAddOnStatus(
                                                                        addon.id,
                                                                        addon.isActive
                                                                    )
                                                                }
                                                                className={`w-11 h-6 rounded-full relative transition-all duration-300 shrink-0
                                                                ${addon.isActive ? "bg-mainBrandColor" : "bg-gray-300"}
                                                                ${(isUpdatingStatus || isReordering) ? "opacity-50 cursor-not-allowed" : ""}`}
                                                            >
                                                                <div
                                                                    className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform
                                                                    ${addon.isActive ? "translate-x-5" : "translate-x-0.5"}`}
                                                                />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        {isReordering && (
                                            <div className="flex justify-end gap-3 pt-4">
                                                <CommonButton
                                                    text="Cancel"
                                                    onClick={() => setIsReordering(false)}
                                                    variant="outline"
                                                />
                                                <CommonButton
                                                    text="Save Order"
                                                    onClick={handleSaveOrder}
                                                    variant="filled"
                                                />
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="bg-lime-50 border border-lime-100 rounded-xl p-4 text-sm text-lime-700">
                                        No active add-ons available.
                                    </div>
                                )}
                            </div>

                            {/* Inactive Add-Ons */}
                            <div>
                                <div className="flex items-center gap-2 mb-4">
                                    <h2 className="text-lg font-bold text-logoHeading">
                                        Inactive Add-Ons
                                    </h2>
                                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-bold">
                                        {inactiveAddOns.length}
                                    </span>
                                </div>

                                {inactiveAddOns.length > 0 ? (
                                    <div className="space-y-4">
                                        {inactiveAddOns.map((addon) => (
                                            <div
                                                key={addon.id}
                                                className={`bg-white rounded-xl border overflow-hidden flex flex-col lg:flex-row items-center p-2 gap-4 ${addon.isActive ? "border-lime-400" : "border-gray-200"
                                                    }`}
                                            >
                                                <div className="w-full lg:w-28 h-40 lg:h-24 overflow-hidden flex-shrink-0 rounded-lg">
                                                    <img
                                                        src={addon.imageUrl || "/placeholder-addon.jpg"}
                                                        alt={addon.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>

                                                <div className="flex-1 p-2 w-full">
                                                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                                                        <div className="min-w-0">
                                                            <h3 className="text-lg md:text-xl font-bold text-logoHeading">
                                                                {addon.name}
                                                            </h3>
                                                            <p className="text-gray-500 text-sm mt-1">
                                                                {addon.description}
                                                            </p>
                                                        </div>

                                                        <div className="flex items-center justify-between lg:justify-end gap-4">
                                                            <span className="text-xl md:text-2xl font-bold text-mainBrandColor whitespace-nowrap">
                                                                AED {addon.price}
                                                            </span>

                                                            <button
                                                                type="button"
                                                                disabled={isUpdatingStatus || isReordering}
                                                                onClick={() =>
                                                                    handleToggleAddOnStatus(
                                                                        addon.id,
                                                                        addon.isActive
                                                                    )
                                                                }
                                                                className={`w-11 h-6 rounded-full relative transition-all duration-300 shrink-0
                                                                ${addon.isActive ? "bg-mainBrandColor" : "bg-gray-300"}
                                                                ${(isUpdatingStatus || isReordering) ? "opacity-50 cursor-not-allowed" : ""}`}
                                                            >
                                                                <div
                                                                    className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform
                                                                    ${addon.isActive ? "translate-x-5" : "translate-x-0.5"}`}
                                                                />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-500">
                                        No inactive add-ons available.
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-20 text-gray-400 bg-white rounded-2xl border border-dashed border-gray-200 font-medium text-sm">
                            <div className="flex flex-col items-center justify-center space-y-2">
                                <Inbox className="w-8 h-8 text-gray-300" />
                                <p className="font-bold text-gray-400">
                                    No add-ons associated with this package yet.
                                </p>
                                <p className="text-xs text-gray-400 font-medium">
                                    Click "Create Add-On" at the top to configure the first structural accessory tier.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // =========================================================
    // VIEW 3: FORM SCREEN INJECTION 
    // =========================================================
    return (
        <AddOnsFormScreen
            mode={selectedAddOnId ? "edit" : "add"}
            packageId={selectedPackage?.id}
            addOnId={selectedAddOnId}
            onClose={() => {
                setSelectedAddOnId(null);
                setView("addons-workspace");
            }}
        />
    );
};

export default ManageAddOns;